import React, { useEffect, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import { Portlet, PortletBody, PortletHeader } from "../../partials/content/Portlet";
import { Grid, Tooltip, IconButton, Stack, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { Loadable, pageStore, TableLevelAction, useGlobalStore, BulkActionIconButton } from '../../common';
import { AddCircle as AddCircleIcon } from '@mui/icons-material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { ProvidersEditDialog } from './ProvidersEditDialog';
import { useFitHeight } from '../../../_metronic/layout/Layout';
import { useStatePtr } from '@pure-ptr/react';
import { ProvidersPageGrid } from './ProvidersPageGrid';
import { errorToastify, infoToastify } from '../../util/toast';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import classNames from 'classnames';
import UpdateNpiRoleDialog from './UpdateNpiRoleDialog';
import { UpdateOutlined } from '@mui/icons-material';

const useStyles = makeStyles(theme => ({
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    icon: {
        fontSize: 17,
        marginTop: 1,
    }
}));

const { usePageStore, useGridFilters } = pageStore('providers-landing', {
    sort: [{ id: 'npi', direction: 'asc' }]
});

export default function ProvidersPage({
    intl,
    selectedClientName,
    history,
    deletePending,
    uploadFilePending,
    pending,
    updateStatusPending,
    actions: {
        providersLanding,
        searchGetByName,
        searchReset,
        resetPagedTable,
        deleteProvider,
        downloadReport,
        uploadFile,
        updateEnableStatus,
        npiRolesLanding
    }
}) {

    useFitHeight();

    const getByNamePending = useGlobalStore( x => x.search.getByName.pending );
    const providersDownloadReportPending = useGlobalStore( x => x.contractProviders.download.pending );
    const providersData = usePageStore((x) => x.list);
    const [file, setFile] = useState(null);
    const importRef = useRef(null);
    const classes = useStyles();
    const [refresh, setRefresh] = useState(Promise.resolve());
    const [openProviderEditDialog, setOpenProviderEditDialog] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const selectedProviderIdsPtr = useStatePtr<number[]>([]);
    const npiRoleRoleDialogOpenPtr = useStatePtr(false);

    useEffect(() => {
        npiRolesLanding();
    }, [npiRolesLanding]);

    useEffect(() => {        
        const pageName = {
            name: 'providers-landing'
        }
        searchGetByName(pageName);

        return function cleanup() {
            resetPagedTable();
            searchReset();
        }
    }, [searchGetByName]);


    function handleLoad(sort, filter, pagination) {
        providersLanding(pagination, sort, filter);
    }

    const tableFilter = useGridFilters(),
        providerPageRecord = tableFilter.pagination;

    function handleDownloadTemplate() {
        const payload = {
            page: null,
            filter: tableFilter.filters || null,
            sort: tableFilter.sort || [],
        };
        downloadReport(payload, downloadTemplateCallback);
    }

    function downloadTemplateCallback(blob) {
        const displayName = `${selectedClientName.replace(/ /g, "_")}_ProvidersReport`;
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = `${displayName.replace(/\s/g, '')}.csv`;
        a.click();
    }

    function handleImportFileInput() {
        importRef.current.click();
    }

    function handleFileChange(e) {
        let tempFile = e.target.files[0];
        if (!tempFile || tempFile.type !== 'text/csv') {
            errorToastify("Only CSV files are supported. Please select a valid .csv file to upload.");
            importRef.current.value = null;
            e.target.value = null;
            return;
        }

        setFile(tempFile);
        uploadFile(tempFile, uploadFileCallback);
        e.target.value = null;
    }
    function uploadFileCallback() {
        infoToastify(<><b>Uploading Providers</b><br/>You'll be notified when it's complete.</>);
        setFile(null);
        handleRefresh();
    }

    function handleOpen() {
        setSelectedData(null);
        setOpenProviderEditDialog(true);
    }

    const handleClose = (reload = false) => {
        setOpenProviderEditDialog(false);

        if(reload === true) {
            handleRefresh();
        }
    }

    const handleProviderEdit = (rowData) => {
        setSelectedData(rowData);
        setOpenProviderEditDialog(true);
    }

    function handleRefresh() {
        setRefresh(Promise.resolve());
    }
    const handleProviderDelete = (rowData) => { 
        deleteProvider(rowData?.id, handleRefresh);
    }

    const updateStatusCallback = () => {
        handleRefresh();
    }

    const handleBulkEnable = (processAll, selectedIds) => {
        const payload = processAll
            ? {
                  page: null,
                  filter: tableFilter.filters || null,
                  sort: tableFilter.sort || [],
              }
            : { ids: selectedIds, isActive: true };

        updateEnableStatus(payload, updateStatusCallback, processAll, true);
    }

    const handleBulkDisable = (processAll, selectedIds) => {
        const payload = processAll
            ? {
                  page: null,
                  filter: tableFilter.filters || null,
                  sort: tableFilter.sort || [],
              }
            : { ids: selectedIds, isActive: false };

        updateEnableStatus(payload, updateStatusCallback, processAll, false);
    }


    const handleBulkUpdateRole = (processAll) => {
        // We'll need to pass processAll to UpdateNpiRoleDialog
        npiRoleRoleDialogOpenPtr.set(true);
    }

    return (
        <>
            <Helmet title="Provider" />
            <Portlet>
                <PortletHeader
                    title="Provider"
                    name="providers-landing"
                    handleRefresh={handleRefresh}
                    showSearchFilters={true}
                    showSearchFiltersDropdown={false}
                />
                <PortletBody>
                <Stack direction="column" spacing={1} sx={{ height: '100%', minHeight: 400 }}>
                        <TableLevelAction
                            handleRefresh={handleRefresh}
                            actions={ () =>
                                <>
                                    <Grid item>
                                        <Tooltip title="Add">
                                            <IconButton aria-label="add" size='small' 
                                                onClick={handleOpen}
                                            >
                                                <AddCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item>
                                        <Tooltip title="Import">
                                            <IconButton aria-label="Import" size='small'
                                                onClick={handleImportFileInput}
                                                disabled={uploadFilePending}
                                            >
                                                <i className="fas fa-upload"></i>
                                                <input
                                                    onChange={handleFileChange}
                                                    ref={importRef}
                                                    type="file"
                                                    hidden
                                                    // accept only csv files
                                                    accept=".csv"
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item>
                                        <Tooltip title="Export">
                                            <IconButton 
                                                aria-label="Export" 
                                                size='small'
                                                onClick={handleDownloadTemplate}
                                                disabled={providersDownloadReportPending}
                                            >
                                                <GetAppIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item>
                                        <BulkActionIconButton
                                            pageName="providers-landing"
                                            actionLabel="Update"
                                            onConfirm={handleBulkUpdateRole}
                                            icon={<UpdateOutlined />}
                                            size="small"
                                            tooltip="Update Provider Role"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <BulkActionIconButton
                                            pageName="providers-landing"
                                            actionLabel="Enable"
                                            onConfirm={handleBulkEnable}
                                            icon={<CheckCircleIcon />}
                                            size="small"
                                            tooltip="Enable"
                                            isPending={updateStatusPending}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <BulkActionIconButton
                                            pageName="providers-landing"
                                            actionLabel="Disable"
                                            onConfirm={handleBulkDisable}
                                            icon={<i className={classNames("fa fa-solid fa-ban", classes.icon)}></i>}
                                            size="small"
                                            tooltip="Disable"
                                            isPending={updateStatusPending}
                                        />
                                    </Grid>
                                </>
                            }
                            
                            loading={providersDownloadReportPending}
                            actionLevelGrid={ () =>
                                <Grid container>
                                    <Grid item md={12}>
                                    <h6 className="">{`Total Records : ${(providersData?.length > 0 && providerPageRecord?.totalRecords) ? providerPageRecord?.totalRecords : 0}`}</h6>
                                    </Grid>
                                </Grid>
                            }
                        />

                        <ProvidersPageGrid
                            defaultCustomFilter={[]}
                            selectedProviderIdsPtr={selectedProviderIdsPtr}
                            data={providersData}
                            loading={ pending || getByNamePending || deletePending }
                            deletePending={deletePending}
                            paginationFilters={tableFilter}
                            onLoad={handleLoad}
                            refresh={refresh}
                            showSearchFilters={true}
                            handleEdit={handleProviderEdit}
                            handleDelete={handleProviderDelete}
                        />
                    </Stack>
                </PortletBody>
            </Portlet>

            <ProvidersEditDialog open={openProviderEditDialog} handleClose={handleClose} data={selectedData}/>
            <UpdateNpiRoleDialog 
                openPtr={npiRoleRoleDialogOpenPtr}
                selectedItemsIdsPtr={selectedProviderIdsPtr}
                handleRefresh={handleRefresh}
            />
        </>
    );
}
